import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, finalize, Subscription, take, tap } from 'rxjs';
import { ISessionLecturerAttendancePostData } from '../../models/session-group/session-lecturer-attendance-post-data.model';
import { ISession } from '../../models/session-group/session.model';
import { IGetLecturerStatisticsByMonthData } from '../../models/statistics/lecturer/get-lecturer-statistics-by-month-data.model';
import { ILecturerStats } from '../../models/statistics/lecturer/lecturer-stats.model';
import { SessionLecturersService } from '../entities/session-lecturers/session-lecturers.service';
import { ISessionsLecturerFilterData, SessionsService } from '../entities/sessions/sessions.service';
import { LecturersService } from '../entities/users/lecturers/lecturers.service';
import { SelectedOrgService } from '../selected-org.service';
import { UtilsService } from '../utils.service';
import { UserStoreService } from './user-store.service';
import { ClosedLecturerAttendanceMonthsService, IClosedLecturerAttendanceMonthToggleData } from '../entities/closed-lecturer-attendance-months/closed-lecturer-attendance-months.service';
import { StoreService } from './store.service';

export interface ILecturerSessionDataItem {
  session: ISession;
  attendanceConflict: boolean;
  conflictedLecturers: {
    [lecturerId: number]: {
      lecturingWith: number[];
      warningEnabled: boolean;
    };
  };
  eventUuid: string;
  shopItemUuid: string;
};

export interface ILecturerSessionData {
  items: ILecturerSessionDataItem[];
  date: string;
  isToday: boolean;
};

export interface ISessionsLecturerState {
  sessionsData$: BehaviorSubject<ILecturerSessionData[]>;
  monthAttendanceClosed$: BehaviorSubject<boolean | null>;
  fetchingSessionsData$: BehaviorSubject<boolean>;
  updatingSessionsData$: BehaviorSubject<number[]>;

  lecturerStats$: BehaviorSubject<ILecturerStats | null>;
  fetchingLecturerStats$: BehaviorSubject<boolean>;
};


@Injectable({
  providedIn: 'root'
})
export class SessionsLecturerStoreService implements OnDestroy {

  private initState: ISessionsLecturerState = {
    sessionsData$: new BehaviorSubject<ILecturerSessionData[]>([]),
    monthAttendanceClosed$: new BehaviorSubject<boolean | null>(null),
    fetchingSessionsData$: new BehaviorSubject<boolean>(false),
    updatingSessionsData$: new BehaviorSubject<number[]>([]),

    lecturerStats$: new BehaviorSubject<ILecturerStats | null>(null),
    fetchingLecturerStats$: new BehaviorSubject<boolean>(false),
  };
  private state = this.utilsService.initializeState(this.initState) as ISessionsLecturerState;

  private admin: boolean = false;
  private inited = false;
  private subs: Subscription[] = [];
  public filters: ISessionsLecturerFilterData | undefined;

  constructor(
    private selectedOrgService: SelectedOrgService,
    private sessionsService: SessionsService,
    private sessionLecturersService: SessionLecturersService,
    private lecturersService: LecturersService,
    private utilsService: UtilsService,
    private userStoreService: UserStoreService,
    private closedLecturerAttendanceMonthsService: ClosedLecturerAttendanceMonthsService,
    private store: StoreService,
  ) {
    this.subs.push(
      this.store.actions.sessionCard_sessionChanged$.subscribe((d) => {
        this.updateSingleItemInState(d.sessionId);
      })
    );
  }

  public reinit() {
    this.init(this.admin, this.filters);
  }

  public init(admin: boolean, filters?: ISessionsLecturerFilterData) {
    this.admin = admin;
    this.reset();


    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;


    this.fetchSessionsData(filters);
    if (!admin) this.fetchLecturerStats(filters);
  }

  public fetchSessionsData(filters?: ISessionsLecturerFilterData, usePrevFilters: boolean = false) {
    this.filters = usePrevFilters ? this.filters : filters;
    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;

    this.state.fetchingSessionsData$.next(true);
    this.getFreshLecturerSessions(orgId).pipe(
      take(1),
      finalize(() => this.state.fetchingSessionsData$.next(false))
    ).subscribe({
      next: (res) => {
        this.state.sessionsData$.next(res);
      }
    });


    if (this.admin) {
      // determine if month attendance is closed
      this.closedLecturerAttendanceMonthsService.isMonthClosed({
        month: this.filters?.month,
        year: this.filters?.year,
        organizationId: orgId,
      }).pipe(
        take(1),
      ).subscribe({
        next: (res) => {
          this.state.monthAttendanceClosed$.next(res);
        }
      })
    }
  }

  public fetchLecturerStats(filters?: ISessionsLecturerFilterData) {
    const orgId = this.selectedOrgService.getCurrentValue();
    const userId = this.userStoreService.state.user$.getValue()?.id;
    if (!orgId || !userId) return;

    const data: IGetLecturerStatisticsByMonthData = {
      year: filters?.year,
      month: filters?.month,
      orgId,
      userId,
    };
    this.state.fetchingLecturerStats$.next(true);
    this.lecturersService.getStatsByMonth(data).pipe(
      take(1),
      finalize(() => this.state.fetchingLecturerStats$.next(false))
    ).subscribe({
      next: (res) => {
        this.state.lecturerStats$.next(res);
      }
    })
  }

  public postSessionLecturerAttendance(data: ISessionLecturerAttendancePostData) {
    return this.sessionLecturersService.postSessionLecturerAttendance(data).pipe(
      take(1),
      tap(() => {
        this.updateSingleItemInState(data.sessionId);
      })
    );
  }

  private updateSingleItemInState(sessionId: number) {
    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;

    this.state.updatingSessionsData$.next([ ...this.state.updatingSessionsData$.getValue(), sessionId ]);
    this.getFreshLecturerSessions(orgId).pipe(
      take(1),
      finalize(() => this.state.updatingSessionsData$.next( this.state.updatingSessionsData$.getValue().filter(x => x !== sessionId)) )
    ).subscribe({
      next: (res) => {
        // replace updated item with fresh one
        const currState = this.state.sessionsData$.getValue();
        const oldDataItemIndex = currState.findIndex(x => x.items.find(y => y.session.id === sessionId));
        if (oldDataItemIndex === -1) return;
        const oldItemIndex = currState[oldDataItemIndex].items.findIndex(x => x.session.id === sessionId);


        const freshDataItemIndex = res.findIndex(x => x.items.find(y => y.session.id === sessionId));
        if (freshDataItemIndex === -1) return;
        const freshItemIndex = res[freshDataItemIndex].items.findIndex(x => x.session.id === sessionId);
        
        const newState = [ ...currState ];
        newState[oldDataItemIndex].items[oldItemIndex] = res[freshDataItemIndex].items[freshItemIndex];
        this.state.sessionsData$.next(newState);
      }
    });
  }

  private getFreshLecturerSessions(orgId: number){
    return this.sessionsService.getLecturerSessions(orgId, this.admin, this.filters)
  }

  public toggleMonthAttendanceClosed() {
    const data: IClosedLecturerAttendanceMonthToggleData = {
      organizationId: this.selectedOrgService.getCurrentValue()!,
      month: this.filters?.month,
      year: this.filters?.year,
      state: this.state.monthAttendanceClosed$.getValue() ? 'OFF' : 'ON',
    };

    return this.closedLecturerAttendanceMonthsService.toggle(data).pipe(
      take(1),
      tap(() => {
        this.reinit();
      })
    );
  }

  private reset() {
    this.utilsService.resetState(this.initState, this.state);
  }

  public getState() {
    return this.state;
  }


  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
